/**
 * Revisão de Proposta com item cancelado parcial: descobre quais LINHAS da proposta
 * pertencem a itens FINALIZADOS (não-editáveis) — pra travar na UI e proteger no apply.
 *
 * Resolve o furo do AGRUPAMENTO: um item agrupado (RENOVAÇÃO grade / HA) guarda só o
 * linha_id da 1ª linha; as demais ficam no JSONB linhas_telefone. Por isso casamos a
 * linha da proposta com o item por linha_id OU por telefone (ddd+linha vs numero).
 * (Douglas 16/06)
 */

export interface ItemPedidoMatch {
  id?: string;
  linha_id?: string | null;
  quadro_atual?: string | null;
  data_ativacao?: string | null;
  linhas_telefone?: any;
}
export interface LinhaPropostaMatch {
  linha_id?: string | null;
  numero?: string | null;
}

const soDigitos = (s: any): string => String(s ?? '').replace(/\D/g, '');

/** Compara dois telefones tolerando DDD presente/ausente (compara por sufixo). */
function mesmoTelefone(a: any, b: any): boolean {
  const da = soDigitos(a);
  const db = soDigitos(b);
  if (da.length < 8 || db.length < 8) return false;
  return da === db || da.endsWith(db) || db.endsWith(da);
}

/** Telefones que o item "possui" (de linhas_telefone: ddd+linha).
 *  Exportada em 24/08: a revisao precisa saber quando um item NAO tem telefone,
 *  porque e' nesse caso que ele e' agregado (bloco de dados) ou adicional — e
 *  perde a identidade entre revisoes se ninguem tratar. */
export function telefonesDoItem(item: ItemPedidoMatch): string[] {
  const arr = Array.isArray(item?.linhas_telefone) ? item.linhas_telefone : [];
  return arr
    .map((lt: any) => soDigitos(`${lt?.ddd ?? ''}${lt?.linha ?? ''}`))
    .filter((d: string) => d.length >= 8);
}

/** Uma linha da proposta pertence a um item se casar por linha_id OU por telefone (agrupados). */
export function linhaPertenceAItem(linha: LinhaPropostaMatch, item: ItemPedidoMatch): boolean {
  if (linha?.linha_id && item?.linha_id && linha.linha_id === item.linha_id) return true;
  const tels = telefonesDoItem(item);
  if (tels.length === 0) return false;
  return tels.some((t) => mesmoTelefone(t, linha?.numero));
}

/**
 * Itens "protegidos" (não podem ser mexidos na revisão):
 * - revisão de cancelado: tudo que NÃO está cancelado (em andamento/finalizado);
 * - fora dela: finalizado/instalado (QUALIDADE ou já ativado).
 * Mesma regra do useRegenerarItensPedido (LEI 15/06).
 */
export function filtrarItensProtegidos<T extends ItemPedidoMatch>(
  itens: T[],
  isRevisaoCancelado: boolean,
): T[] {
  return (itens || []).filter((i) =>
    isRevisaoCancelado
      ? (i.quadro_atual ?? '') !== 'PEDIDO_CANCELADO'
      : (i.quadro_atual ?? '') === 'QUALIDADE' || i.data_ativacao != null,
  );
}

/** Conjunto de linha_ids da proposta que pertencem a algum item protegido (cobre agrupados). */
export function linhasProtegidasPorMatch(
  itensProtegidos: ItemPedidoMatch[],
  linhasProposta: LinhaPropostaMatch[],
): Set<string> {
  const set = new Set<string>();
  for (const linha of linhasProposta || []) {
    if (!linha?.linha_id) continue;
    if ((itensProtegidos || []).some((it) => linhaPertenceAItem(linha, it))) {
      set.add(linha.linha_id);
    }
  }
  return set;
}

/** Atalho p/ a UI: dado todos os itens + linhas + se é revisão de cancelado, retorna os linha_ids travados. */
export function computarLinhasTravadas(
  itens: ItemPedidoMatch[],
  linhasProposta: LinhaPropostaMatch[],
  isRevisaoCancelado: boolean,
  itensOutrosPedidos: ItemPedidoMatch[] = [],
): Set<string> {
  // itensOutrosPedidos = itens de OUTROS pedidos do lead (familia movel/fixa): SEMPRE
  // protegidos, pra revisar um pedido nao deixar editar/inserir linha de outro. (25/06)
  const protegidos = [...filtrarItensProtegidos(itens, isRevisaoCancelado), ...(itensOutrosPedidos || [])];
  return linhasProtegidasPorMatch(protegidos, linhasProposta);
}
